import appConfig, { LOCAL_STORAGE_DEVICE_ID_KEY } from "@config/app";
import { OhApi } from "@shared/libs/siayuda";
import { client } from "hassibot/services_v2";
import { DeviceId } from "hassibot/services_v2/common/types";
import { registerDeviceReader } from "hassibot/services_v2/login/reader";
import { routes } from "hassibot/services_v2/login/routes";
import { RegisterDeviceResponse } from "hassibot/services_v2/login/types";
import { registerDeviceWriter } from "hassibot/services_v2/login/writer";
import {
  isDeploymentOptInEnabled,
  restoreDeploymentOptInAfterReset,
} from "hassibot/services_v2/service-worker/deployment-opt-in-preference";
import { getCurrentPushSubscription } from "hassibot/services_v2/service-worker/push-subscription";

import { msalInstance } from "./msal";

/**
 * Register device with Ouihelp API.
 * Registers device ID and push token.
 *
 * @param deviceId - Device UUID
 * @param pushToken - Push notification token (null if not available)
 * @returns Promise with device registration data (minimal response)
 */
export const registerDevice = (
  deviceId: DeviceId,
  pushToken: string | null
): Promise<OhApi.ApiResponse<RegisterDeviceResponse>> =>
  client
    .postJson(
      routes.registerDevice(),
      registerDeviceWriter({
        deviceId,
        pushToken,
        deploymentOptIn: isDeploymentOptInEnabled(),
      })
    )
    .then(OhApi.applyReader(registerDeviceReader));

/**
 * Re-register the device using its *current* push subscription (read-only — never
 * creates one) so the stored push token is preserved. Goes straight through
 * `registerDevice` (not the `useRegisterDevice` mutation), so it is toast-free and
 * meant to be called best-effort — e.g. to push a fresh deployment opt-in
 * immediately when the user flips the footer toggle.
 */
export const registerDeviceWithCurrentPushToken = async (): Promise<
  OhApi.ApiResponse<RegisterDeviceResponse>
> => {
  const subscription = await getCurrentPushSubscription();
  const pushToken = subscription ? JSON.stringify(subscription) : null;
  return registerDevice(appConfig.DEVICE_ID, pushToken);
};

/**
 * Logout using Microsoft SSO.
 * With stateless JWT authentication, logout is purely client-side:
 * - Clears MSAL cache
 * - Clears localStorage
 * - Clears sessionStorage
 *
 * @returns Promise that resolves when logout is complete
 */
export const logout = (): Promise<void> => {
  localStorage.clear();
  sessionStorage.clear();

  // Restore device ID after clearing
  localStorage.setItem(LOCAL_STORAGE_DEVICE_ID_KEY, appConfig.DEVICE_ID);

  // Keep the deployment opt-in preference across logout (same survival trick).
  restoreDeploymentOptInAfterReset();

  return msalInstance.logoutRedirect({
    onRedirectNavigate: () => false, // Stay on same page
  });
};
